import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, ArrowRight } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { PAGE_META, BUSINESS_ADDRESS } from "@/lib/seo-constants";
import { webPageSchema, breadcrumbListSchema } from "@/lib/jsonld";

// Bump both when the policy text materially changes — the displayed date is
// the visitor's only signal that terms moved.
const EFFECTIVE_DATE = "August 18, 2026";
const LAST_UPDATED = "August 18, 2026";

const SECTIONS = [
    { id: "scope", label: "1. Who this covers" },
    { id: "collect", label: "2. What we collect" },
    { id: "use", label: "3. How we use it" },
    { id: "consent", label: "4. Consent & withdrawal" },
    { id: "cookies", label: "5. Cookies & analytics" },
    { id: "chat", label: "6. Our chat assistant" },
    { id: "sharing", label: "7. Who we share it with" },
    { id: "borders", label: "8. Storage outside Canada" },
    { id: "retention", label: "9. How long we keep it" },
    { id: "security", label: "10. How we protect it" },
    { id: "rights", label: "11. Your rights" },
    { id: "children", label: "12. Children" },
    { id: "links", label: "13. Third-party links" },
    { id: "changes", label: "14. Changes to this policy" },
    { id: "contact", label: "15. Contact us" },
];

const Block = ({ id, heading, children }) => (
    <section
        id={id}
        className="scroll-mt-28 border-t border-brand-dark/10 pt-10 first:border-t-0 first:pt-0"
        data-testid={`privacy-section-${id}`}
    >
        <h2 className="font-display text-2xl sm:text-3xl tracking-wide text-brand-dark leading-tight mb-4">
            {heading}
        </h2>
        <div className="space-y-4 text-[15px] leading-relaxed text-brand-dark/80">
            {children}
        </div>
    </section>
);

const Bullets = ({ items }) => (
    <ul className="space-y-2.5 pl-0">
        {items.map((item, i) => (
            <li key={i} className="flex gap-3">
                <span
                    className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-brand-coral"
                    aria-hidden="true"
                />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const StorageRow = ({ name, kind, purpose, life }) => (
    <div className="border-l-2 border-brand-cyan/60 pl-4 py-1">
        <p className="font-semibold text-brand-dark text-sm">
            {name}{" "}
            <span className="font-normal text-brand-dark/55 uppercase tracking-wider text-[11px]">
                · {kind}
            </span>
        </p>
        <p className="mt-1 text-sm text-brand-dark/75">{purpose}</p>
        <p className="mt-0.5 text-xs text-brand-dark/60 italic">{life}</p>
    </div>
);

export default function Privacy() {
    return (
        <>
            <Seo
                {...PAGE_META.privacy}
                path="/privacy"
                jsonLd={[
                    webPageSchema({
                        name: PAGE_META.privacy.title,
                        description: PAGE_META.privacy.description,
                        path: "/privacy",
                    }),
                    breadcrumbListSchema([
                        { name: "Privacy Policy", path: "/privacy" },
                    ]),
                ]}
            />

            {/* Hero */}
            <Section dark className="py-20 md:py-24" data-testid="privacy-hero">
                <Container>
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                            Legal
                        </p>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                            data-testid="privacy-title"
                        >
                            Privacy
                            <br />
                            <span className="text-brand-coral">Policy.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed">
                            What we collect when you ask us for a quote, why we
                            collect it, and how to get it back or have it
                            deleted. Written in plain language — no fine print.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-wider text-white/50">
                            <span data-testid="privacy-effective">
                                Effective {EFFECTIVE_DATE}
                            </span>
                            <span data-testid="privacy-updated">
                                Last updated {LAST_UPDATED}
                            </span>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* Body */}
            <Section bg="white" className="py-14 md:py-20" data-testid="privacy-body">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        {/* Table of contents */}
                        <aside className="lg:col-span-4" data-testid="privacy-toc">
                            <div className="lg:sticky lg:top-28">
                                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-4">
                                    On This Page
                                </p>
                                <ul className="space-y-1.5 border-l border-brand-dark/10 pl-4">
                                    {SECTIONS.map((s) => (
                                        <li key={s.id}>
                                            <a
                                                href={`#${s.id}`}
                                                className="text-sm text-brand-dark/70 hover:text-brand-coral transition-colors duration-200"
                                                data-testid={`privacy-toc-${s.id}`}
                                            >
                                                {s.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-8 border border-brand-dark/10 bg-brand-offwhite p-5">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-brand-dark font-semibold mb-2">
                                        <ShieldCheck
                                            className="h-4 w-4 text-brand-cyan"
                                            strokeWidth={2.25}
                                        />
                                        The short version
                                    </div>
                                    <p className="text-sm text-brand-dark/75 leading-relaxed">
                                        We collect what we need to quote and
                                        complete your project. We don't sell
                                        your information, and we don't send
                                        marketing you didn't ask for. Email{" "}
                                        <a
                                            href={COMPANY.emailHref}
                                            className="text-brand-coral font-semibold hover:underline"
                                        >
                                            {COMPANY.email}
                                        </a>{" "}
                                        any time to see, correct, or delete what
                                        we hold.
                                    </p>
                                </div>
                            </div>
                        </aside>

                        {/* Policy text */}
                        <div className="lg:col-span-8 space-y-10">
                            <Block id="scope" heading="1. Who this policy covers">
                                <p>
                                    This policy explains how {COMPANY.name}{" "}
                                    ("Reformity", "we", "us") handles personal
                                    information collected through{" "}
                                    <strong>reformityinc.com</strong>, our quote
                                    forms, our on-site chat assistant, phone and
                                    email conversations, and the work we do at
                                    your property.
                                </p>
                                <p>
                                    We are a contracting business serving the
                                    Greater Toronto Area, with our head office
                                    at the address in section 15. We handle
                                    personal information
                                    in accordance with Canada's{" "}
                                    <em>
                                        Personal Information Protection and
                                        Electronic Documents Act
                                    </em>{" "}
                                    (PIPEDA) and applicable Ontario law.
                                </p>
                                <p>
                                    "Personal information" means information
                                    about an identifiable individual — your
                                    name, contact details, property address, and
                                    the details of the work you're asking us to
                                    do. It does not include aggregated or
                                    anonymized statistics that can't be traced
                                    back to you.
                                </p>
                            </Block>

                            <Block id="collect" heading="2. What we collect">
                                <p className="font-semibold text-brand-dark">
                                    Information you give us directly
                                </p>
                                <Bullets
                                    items={[
                                        <>
                                            <strong>Quote requests.</strong>{" "}
                                            Your name, email address, phone
                                            number, city, postal code, the
                                            services you're interested in,
                                            property type, project size and
                                            description, timeline, best time to
                                            reach you, and how you heard about
                                            us. Commercial and industrial
                                            requests also ask for your role and
                                            the number of sites involved.
                                        </>,
                                        <>
                                            <strong>Chat conversations.</strong>{" "}
                                            Anything you type into the chat
                                            assistant, including any contact or
                                            project details you choose to share
                                            there.
                                        </>,
                                        <>
                                            <strong>
                                                Calls, texts and emails.
                                            </strong>{" "}
                                            Your contact details and the content
                                            of what you send us.
                                        </>,
                                        <>
                                            <strong>Project information.</strong>{" "}
                                            Site details, measurements, and
                                            photographs of the work area taken
                                            during estimates and construction.
                                            We only publish project photos in
                                            our gallery or marketing with the
                                            property owner's permission, and we
                                            never publish street addresses.
                                        </>,
                                        <>
                                            <strong>Billing details.</strong>{" "}
                                            Invoicing information and payment
                                            records for customers. Card and bank
                                            details, when used, are handled by
                                            our payment processor — we don't
                                            store full card numbers.
                                        </>,
                                    ]}
                                />

                                <p className="font-semibold text-brand-dark pt-2">
                                    Information collected automatically
                                </p>
                                <Bullets
                                    items={[
                                        <>
                                            <strong>Usage data.</strong> Pages
                                            viewed, referring site, approximate
                                            region, browser and device type, and
                                            the campaign tags (UTM parameters) on
                                            the link that brought you here.
                                        </>,
                                        <>
                                            <strong>Server logs.</strong> Our
                                            hosting provider records standard
                                            request logs, including IP
                                            addresses, for security and
                                            troubleshooting.
                                        </>,
                                        <>
                                            <strong>
                                                Chat session signals.
                                            </strong>{" "}
                                            A one-way hashed version of your IP
                                            address and your browser's user-agent
                                            string, used to rate-limit abuse of
                                            the chat assistant. We do not store
                                            your raw IP address against your chat
                                            transcript.
                                        </>,
                                        <>
                                            <strong>
                                                Cookies and local storage.
                                            </strong>{" "}
                                            See section 5.
                                        </>,
                                    ]}
                                />
                                <p>
                                    We don't ask for — and we ask that you don't
                                    send us — government ID numbers, health
                                    information, or financial account
                                    credentials through the website or chat.
                                </p>
                            </Block>

                            <Block id="use" heading="3. How we use it">
                                <Bullets
                                    items={[
                                        "To prepare your estimate and respond to your enquiry, usually within one business day.",
                                        "To schedule, plan, and complete the work at your property, including passing the relevant details to the crew or a subcontracted trade assigned to your job.",
                                        "To order materials and coordinate suppliers and permits for your project.",
                                        "To invoice you and keep the business and tax records the law requires us to keep.",
                                        "To follow up on warranty questions and service requests after the job is done.",
                                        "To ask you for a review after your project is complete — you can decline, and we'll only ask once.",
                                        "To understand which pages and campaigns bring people to the site, using aggregated analytics.",
                                        "To protect the site against spam, abuse, and fraudulent submissions.",
                                    ]}
                                />
                                <p>
                                    We do not sell, rent, or trade personal
                                    information, and we do not use it to build
                                    advertising profiles or share it with data
                                    brokers.
                                </p>
                            </Block>

                            <Block id="consent" heading="4. Consent & withdrawal">
                                <p>
                                    When you submit a quote request, start a
                                    chat, or call us, you consent to us using
                                    your information for the purposes described
                                    above. For anything outside those
                                    purposes — for example, using a photo of
                                    your finished project in our marketing — we
                                    ask you first.
                                </p>
                                <p>
                                    You can withdraw your consent at any time by
                                    emailing{" "}
                                    <a
                                        href={COMPANY.emailHref}
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        {COMPANY.email}
                                    </a>
                                    . Withdrawal is subject to legal and
                                    contractual limits: if we're mid-project, we
                                    still need enough information to finish the
                                    work, honour the warranty, and keep the
                                    financial records the law requires.
                                </p>
                            </Block>

                            <Block id="cookies" heading="5. Cookies & analytics">
                                <p>
                                    This site uses a small number of cookies and
                                    browser-storage entries. None of them are
                                    used for advertising or cross-site tracking
                                    by us.
                                </p>
                                <div className="space-y-4 py-2">
                                    <StorageRow
                                        name="preferredTrack"
                                        kind="Cookie · functional"
                                        purpose="Remembers whether you chose the residential, commercial, or industrial side of the site, so you land in the right place next visit. You can always switch back from the home page."
                                        life="Expires after 180 days."
                                    />
                                    <StorageRow
                                        name="reformity.chat.session_id"
                                        kind="Local storage · functional"
                                        purpose="Keeps your chat conversation attached to the right session if you refresh or navigate to another page."
                                        life="Stays until you clear your browser storage."
                                    />
                                    <StorageRow
                                        name="Blog CTA dismissal"
                                        kind="Session storage · functional"
                                        purpose="Remembers that you closed the quote prompt on a blog article so it doesn't reappear."
                                        life="Cleared when you close the tab."
                                    />
                                    <StorageRow
                                        name="Google Analytics (_ga, _ga_*)"
                                        kind="Cookie · analytics"
                                        purpose="Google Analytics 4 gives us aggregated statistics — visits, popular pages, traffic sources, and whether a quote form was completed. IP addresses are anonymized by Google before storage, and we have not enabled Google advertising or remarketing features."
                                        life="Set by Google; typically expires after up to 2 years."
                                    />
                                </div>
                                <p>
                                    You can block or delete cookies in your
                                    browser settings, and you can opt out of
                                    Google Analytics entirely with Google's{" "}
                                    <a
                                        href="https://tools.google.com/dlpage/gaoptout"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        browser opt-out add-on
                                    </a>
                                    . Blocking the functional entries above won't
                                    break the site — you'll just have to
                                    re-select your options each visit.
                                </p>
                            </Block>

                            <Block id="chat" heading="6. Our chat assistant">
                                <p>
                                    The chat window on this site is an automated
                                    assistant, not a person. It answers common
                                    questions about our services and can hand you
                                    off to a quote request. Messages are
                                    processed by a third-party AI provider
                                    (Anthropic) to generate the reply, and the
                                    transcript is stored on our server so our
                                    team can follow up on a real enquiry.
                                </p>
                                <p>
                                    Because a human on our team may read the
                                    conversation, please don't type anything into
                                    chat you wouldn't put in an email — no
                                    payment details, no ID numbers, no
                                    passwords. If you'd rather not use it, call
                                    us at{" "}
                                    <a
                                        href={COMPANY.phoneHref}
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        {COMPANY.phone}
                                    </a>{" "}
                                    or use the{" "}
                                    <Link
                                        to="/contact"
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        contact form
                                    </Link>{" "}
                                    instead.
                                </p>
                            </Block>

                            <Block id="sharing" heading="7. Who we share it with">
                                <p>
                                    We share personal information only with
                                    parties who need it to deliver your project
                                    or run the business, and only to the extent
                                    they need it:
                                </p>
                                <Bullets
                                    items={[
                                        <>
                                            <strong>
                                                Our crew and subcontracted trades.
                                            </strong>{" "}
                                            Your name, address, contact number,
                                            and scope of work — so they can reach
                                            the site and do the job.
                                        </>,
                                        <>
                                            <strong>Suppliers.</strong> Delivery
                                            addresses and scheduling details for
                                            materials going to your property.
                                        </>,
                                        <>
                                            <strong>
                                                Technology providers.
                                            </strong>{" "}
                                            Website and database hosting, email
                                            delivery, our AI chat provider, and
                                            Google Analytics. These providers
                                            process information on our
                                            instructions under contract.
                                        </>,
                                        <>
                                            <strong>
                                                Our lead-management system.
                                            </strong>{" "}
                                            Quote submissions are forwarded into
                                            the internal system we use to track
                                            and follow up on enquiries.
                                        </>,
                                        <>
                                            <strong>
                                                Professional advisers.
                                            </strong>{" "}
                                            Our accountant, insurer, and legal
                                            counsel where required.
                                        </>,
                                        <>
                                            <strong>
                                                Authorities, when required.
                                            </strong>{" "}
                                            Municipalities for permit
                                            applications, and law enforcement or
                                            regulators where we're legally
                                            obligated to disclose.
                                        </>,
                                    ]}
                                />
                                <p>
                                    If the business is ever sold or merged,
                                    customer records may transfer to the new
                                    owner, who would remain bound by this policy
                                    until it is replaced with notice to you.
                                </p>
                            </Block>

                            <Block id="borders" heading="8. Storage outside Canada">
                                <p>
                                    Some of our service providers — including our
                                    analytics and AI chat providers — store or
                                    process data on servers located outside
                                    Canada, primarily in the United States. While
                                    information is in another country, it may be
                                    accessible to the courts, law enforcement, and
                                    national security authorities of that country
                                    under its laws.
                                </p>
                                <p>
                                    We select providers that offer comparable
                                    protection to what's required here, and we
                                    keep the amount of personal information sent
                                    to them to the minimum needed.
                                </p>
                            </Block>

                            <Block id="retention" heading="9. How long we keep it">
                                <Bullets
                                    items={[
                                        "Quote requests that don't become projects: kept for up to 2 years, then deleted.",
                                        "Customer project files, contracts, and invoices: kept for 7 years after the final invoice, to satisfy Canada Revenue Agency record-keeping requirements and to support warranty claims.",
                                        "Chat transcripts: kept for up to 12 months, unless the conversation became a quote request — in which case it follows the quote retention period above.",
                                        "Analytics data: retained by Google on the standard retention setting for the property.",
                                        "Server logs: kept for a short operational window and then rotated out.",
                                    ]}
                                />
                                <p>
                                    When information is no longer needed for
                                    these purposes, we delete it or render it
                                    anonymous.
                                </p>
                            </Block>

                            <Block id="security" heading="10. How we protect it">
                                <p>
                                    The site is served over encrypted HTTPS.
                                    Quote submissions and chat transcripts are
                                    stored in an access-controlled database, and
                                    our internal admin tools require
                                    authentication. Access is limited to the
                                    people who need it to do their job.
                                </p>
                                <p>
                                    No system is perfectly secure. If a breach
                                    ever creates a real risk of significant harm
                                    to you, we will notify you and the Office of
                                    the Privacy Commissioner of Canada as
                                    required by law.
                                </p>
                            </Block>

                            <Block id="rights" heading="11. Your rights">
                                <p>Under PIPEDA, you can ask us to:</p>
                                <Bullets
                                    items={[
                                        "Tell you what personal information we hold about you and how it has been used or disclosed.",
                                        "Give you a copy of that information.",
                                        "Correct anything that's inaccurate or incomplete.",
                                        "Delete information we no longer have a legal or contractual reason to keep.",
                                        "Stop contacting you, or withdraw a consent you previously gave.",
                                    ]}
                                />
                                <p>
                                    Email{" "}
                                    <a
                                        href={COMPANY.emailHref}
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        {COMPANY.email}
                                    </a>{" "}
                                    with the subject line "Privacy request", or
                                    write to the head office address in section
                                    15. We may ask you to confirm your identity before
                                    releasing records, and we'll respond within
                                    30 days. There's no charge for a reasonable
                                    request; if a request would take substantial
                                    work, we'll tell you the cost before starting.
                                </p>
                                <p>
                                    If you're not satisfied with how we handled
                                    your request, you can complain to the{" "}
                                    <a
                                        href="https://www.priv.gc.ca/en/report-a-concern/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-brand-coral font-semibold hover:underline"
                                    >
                                        Office of the Privacy Commissioner of
                                        Canada
                                    </a>
                                    .
                                </p>
                            </Block>

                            <Block id="children" heading="12. Children">
                                <p>
                                    Our services are sold to property owners and
                                    businesses. This site isn't directed at
                                    children, and we don't knowingly collect
                                    personal information from anyone under 18. If
                                    you believe a child has submitted information
                                    to us, contact us and we'll delete it.
                                </p>
                            </Block>

                            <Block id="links" heading="13. Third-party links">
                                <p>
                                    Our site links out to review platforms
                                    (Google, HomeStars, Bark, Houzz, BBB), social
                                    media, and occasionally to manufacturer or
                                    municipal pages. Once you follow one of those
                                    links, you're on someone else's site under
                                    their privacy policy — including any review
                                    you choose to publish there, which is public
                                    by design. We don't control those sites and
                                    aren't responsible for their practices.
                                </p>
                            </Block>

                            <Block id="changes" heading="14. Changes to this policy">
                                <p>
                                    We may update this policy as our services or
                                    tools change. The effective date at the top of
                                    the page always reflects the current version.
                                    If a change materially affects how we use
                                    information you've already given us, we'll
                                    notify you directly where we have your contact
                                    details.
                                </p>
                            </Block>

                            <Block id="contact" heading="15. Contact us">
                                <p>
                                    Questions about this policy, or about your
                                    information? Our Privacy Officer is reachable
                                    at:
                                </p>
                                <div className="border border-brand-dark/10 bg-brand-offwhite p-6 space-y-3">
                                    <p className="font-display text-xl tracking-wide text-brand-dark">
                                        {COMPANY.name} — Privacy Officer
                                    </p>
                                    <a
                                        href={COMPANY.emailHref}
                                        className="flex items-center gap-3 text-sm text-brand-dark hover:text-brand-coral transition-colors"
                                        data-testid="privacy-email-link"
                                    >
                                        <Mail
                                            className="h-4 w-4 text-brand-coral shrink-0"
                                            strokeWidth={2.25}
                                        />
                                        {COMPANY.email}
                                    </a>
                                    <a
                                        href={COMPANY.phoneHref}
                                        className="flex items-center gap-3 text-sm text-brand-dark hover:text-brand-coral transition-colors"
                                        data-testid="privacy-phone-link"
                                    >
                                        <Phone
                                            className="h-4 w-4 text-brand-coral shrink-0"
                                            strokeWidth={2.25}
                                        />
                                        {COMPANY.phone}
                                    </a>
                                    <p className="flex items-start gap-3 text-sm text-brand-dark/75">
                                        <MapPin
                                            className="h-4 w-4 mt-0.5 text-brand-coral shrink-0"
                                            strokeWidth={2.25}
                                        />
                                        <span>
                                            {BUSINESS_ADDRESS.streetAddress}
                                            <br />
                                            {BUSINESS_ADDRESS.addressLocality},{" "}
                                            {BUSINESS_ADDRESS.addressRegion}{" "}
                                            {BUSINESS_ADDRESS.postalCode}
                                            <br />
                                            Canada
                                        </span>
                                    </p>
                                </div>
                            </Block>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* CTA */}
            <Section dark className="py-14 md:py-20" data-testid="privacy-cta">
                <Container>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="max-w-xl">
                            <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-white leading-tight">
                                Ready to start your project?
                            </h2>
                            <p className="mt-3 text-white/70 text-base leading-relaxed">
                                Free quotes, no obligation — and we only use what
                                you send us to price the work.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <BrandButton asChild variant="primary" size="md">
                                <Link to="/contact">
                                    Get a Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton asChild variant="outline" tone="onDark" size="md">
                                <a href={COMPANY.phoneHref}>
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {COMPANY.phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
